import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Download, X, Smartphone, Monitor, Apple } from "lucide-react";

export default function InstallPrompt() {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches || 
                                window.navigator.standalone === true;
    setIsStandalone(isInStandaloneMode);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Listen for beforeinstallprompt event (Android/Desktop)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt if not dismissed before
      const dismissed = localStorage.getItem('installPromptDismissed');
      if (!dismissed) {
        setShowPrompt(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      console.log('PWA was installed');
      setShowPrompt(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`User response: ${outcome}`);

    if (outcome === 'accepted') {
      setShowPrompt(false);
    }

    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('installPromptDismissed', 'true');
    // Show again after 7 days
    setTimeout(() => {
      localStorage.removeItem('installPromptDismissed');
    }, 7 * 24 * 60 * 60 * 1000);
  };

  // Don't show if already installed
  if (isStandalone) {
    return null;
  }

  // iOS install instructions
  if (isIOS && !showPrompt) {
    return null; // Don't auto-show for iOS
  }

  if (isIOS) {
    return (
      <Card className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 shadow-lg border-2 border-primary">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Apple className="w-6 h-6 text-primary flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-bold text-sm mb-2">{t('installPrompt.title')}</h3>
              <p className="text-xs text-muted-foreground mb-3">
                {t('installPrompt.iosIntro')}
              </p>
              <ol className="text-xs text-muted-foreground space-y-1 mb-3">
                <li>{t('installPrompt.iosStep1')} <span className="text-primary">⎙</span></li>
                <li>{t('installPrompt.iosStep2')}</li>
                <li>{t('installPrompt.iosStep3')}</li>
              </ol>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="flex-shrink-0"
              onClick={handleDismiss}
              aria-label={t('installPrompt.dismiss')}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Android/Desktop install prompt
  if (!showPrompt || !deferredPrompt) {
    return null;
  }

  return (
    <Card className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 shadow-lg border-2 border-primary animate-in slide-in-from-bottom-5">
      <CardContent className="pt-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Download className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-sm mb-1">{t('installPrompt.title')}</h3>
            <p className="text-xs text-muted-foreground mb-3">
              {t('installPrompt.description')}
            </p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
              <Monitor className="w-4 h-4" />
              <span>{t('installPrompt.worksOn')}</span>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleInstallClick}
                className="flex-1 gap-2"
                style={{ background: '#007A33' }}
              >
                <Download className="w-4 h-4" />
                {t('installPrompt.installNow')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleDismiss}
              >
                {t('installPrompt.later')}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
